import type { StatBlock } from '@blogforge/shared';
import type { DragPath } from '../../types';
import { useEditorStore } from '../../stores/editorStore';

// ─────────────────────────────────────────────────────────────────
// Inspector for the stat block. Without this the block could render but
// never be filled in — `stats` is empty by default and nothing else in
// the editor writes to it.
//
// There is deliberately no control for `attrs.animateOnScroll`: the
// count-up is not implemented anywhere yet (published output does not
// render stat blocks at all), and a toggle that changed nothing would be
// a lie in the UI. The attribute stays in the schema for when the
// published renderer lands.
// ─────────────────────────────────────────────────────────────────

interface StatInspectorProps {
  block: StatBlock;
  path: DragPath;
}

/** One figure, derived from the schema so the two can never drift. */
type StatItem = StatBlock['attrs']['stats'][number];

export function StatInspector({ block, path }: StatInspectorProps) {
  const updateBlock = useEditorStore((s) => s.updateBlock);
  // Defensive: blocks reach the store unvalidated (see StatRenderer).
  const stats: StatItem[] = block.attrs?.stats ?? [];
  const layout = block.attrs?.layout === 'grid' ? 'grid' : 'row';

  const set = (patch: Partial<StatBlock['attrs']>) => {
    updateBlock(block.id, { attrs: { ...block.attrs, ...patch } }, path);
  };

  const setStat = (index: number, patch: Partial<StatItem>) => {
    set({ stats: stats.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
  };

  const addStat = () => {
    set({
      stats: [
        ...stats,
        // Placeholders only — an empty value renders as nothing on the canvas,
        // which is exactly the symptom this inspector exists to fix.
        { value: '100+', label: 'New stat', description: '', icon: '' },
      ],
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="field-label">Heading (optional)</label>
        <input
          type="text"
          className="field-input"
          placeholder="By the numbers"
          value={block.content ?? ''}
          onChange={(e) => updateBlock(block.id, { content: e.target.value }, path)}
        />
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <label className="field-label mb-0 font-medium">Stats</label>
          <button
            type="button"
            className="text-xs font-semibold text-accent hover:text-ink"
            onClick={addStat}
          >
            + Add stat
          </button>
        </div>

        {stats.length === 0 ? (
          <div className="rounded-lg border border-dashed border-line bg-bg-soft p-6 text-center text-sm text-muted">
            <p>No stats added yet</p>
            <button
              type="button"
              className="mt-2 text-accent hover:text-primary"
              onClick={addStat}
            >
              Add your first stat
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {stats.map((stat, index) => (
              <div key={index} className="rounded-lg border border-line p-4">
                <div className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-medium text-ink-soft">Stat {index + 1}</span>
                  <button
                    type="button"
                    className="text-sm text-danger hover:text-danger"
                    onClick={() => set({ stats: stats.filter((_, i) => i !== index) })}
                  >
                    Remove
                  </button>
                </div>

                <div className="space-y-3">
                  <div className="flex gap-2">
                    <div className="w-16">
                      <label className="field-label text-xs">Icon</label>
                      <input
                        type="text"
                        className="field-input text-center"
                        placeholder="🚀"
                        value={stat.icon || ''}
                        onChange={(e) => setStat(index, { icon: e.target.value })}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="field-label text-xs">Value</label>
                      <input
                        type="text"
                        className="field-input"
                        placeholder="12,000"
                        value={stat.value || ''}
                        onChange={(e) => setStat(index, { value: e.target.value })}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="field-label text-xs">Label</label>
                    <input
                      type="text"
                      className="field-input"
                      placeholder="Happy readers"
                      value={stat.label || ''}
                      onChange={(e) => setStat(index, { label: e.target.value })}
                    />
                  </div>

                  <div>
                    <label className="field-label text-xs">Description (optional)</label>
                    <textarea
                      className="field-input min-h-[60px]"
                      placeholder="Since we launched in 2024"
                      value={stat.description || ''}
                      onChange={(e) => setStat(index, { description: e.target.value })}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <label className="field-label mb-0">Layout</label>
        <select
          className="field-input"
          value={layout}
          onChange={(e) => set({ layout: e.target.value as 'row' | 'grid' })}
        >
          <option value="row">Row — one line of figures</option>
          <option value="grid">Grid — wraps into columns</option>
        </select>
      </div>
    </div>
  );
}
